import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Import language files
import enTranslations from '../locales/en.json';
import deTranslations from '../locales/de.json';
import plTranslations from '../locales/pl.json';
import esTranslations from '../locales/es.json';

// Define supported languages
export const SUPPORTED_LANGUAGES = {
  en: { name: 'English', flag: '🇺🇸' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  pl: { name: 'Polski', flag: '🇵🇱' },
  es: { name: 'Español', flag: '🇪🇸' }
} as const;

export type LanguageCode = keyof typeof SUPPORTED_LANGUAGES;

// Combine all translations
const translations = {
  en: enTranslations,
  de: deTranslations,
  pl: plTranslations,
  es: esTranslations
};

interface LanguageContextType {
  currentLanguage: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: (key: string) => any;
  supportedLanguages: typeof SUPPORTED_LANGUAGES;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Helper function to get nested object value by dot notation
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>(() => {
    try {
      const savedLanguage = localStorage.getItem('language') as LanguageCode;
      if (savedLanguage && SUPPORTED_LANGUAGES[savedLanguage]) {
        return savedLanguage;
      }
    } catch (error) {
      console.warn('Failed to load language from localStorage:', error);
    }
    return 'en';
  });

  // Save language to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('language', currentLanguage);
    } catch (error) {
      console.warn('Failed to save language to localStorage:', error);
    }
  }, [currentLanguage]);

  const setLanguage = (language: LanguageCode) => {
    setCurrentLanguage(language);
  };

  const t = (key: string): any => {
    const translation = getNestedValue(translations[currentLanguage], key);
    if (translation === undefined) {
      // Fallback to English if translation not found
      const englishTranslation = getNestedValue(translations.en, key);
      if (englishTranslation === undefined) {
        console.warn(`Translation key not found: ${key}`);
        return key;
      }
      return englishTranslation;
    }
    return translation;
  };



  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    t,
    supportedLanguages: SUPPORTED_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
